import { call, put, take, select, takeLatest, takeEvery } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'
import {collection, query, where, orderBy, limit, doc, updateDoc, writeBatch, serverTimestamp} from 'firebase/firestore'
import { db } from '../../lib/firebase'
import { createSnapshotChannel } from '../utils/createSnapshotChannel'
import { getDocs } from 'firebase/firestore'
import {
    markWatchedSaga,
    markProjectWatchedSaga,
    markNotificationWatchedSaga,
    markAllWatchedSaga,
    setNotifications,
    setLoading,
    clearNotifications,
    setAllNotifications,
    setLoadingFull,
    clearAllNotifications,
    type Notification
} from './notificationsSlice'
import { logTiming } from '@/utils/timing'

function* markWatchedWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const taskId = action.payload
    const state: any = yield select((s: any) => s.notifications)
    const matches: Notification[] = state.allIds
        .map((id: string) => state.byId[id])
        .filter((n: Notification) => n && n.taskId === taskId)

    if (matches.length === 0) return

    try {
        for (const notification of matches) {
            yield call(() => updateDoc(doc(db, 'notifications', notification.id), { watched: true, watchedAt: serverTimestamp() }))
        }
    } catch (err) {
        console.error('Error marking notifications as watched:', err)
    }
}

function* markProjectWatchedWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const projectId = action.payload
    const state: any = yield select((s: any) => s.notifications)
    const matches: Notification[] = state.allIds
        .map((id: string) => state.byId[id])
        .filter((n: Notification) => n && n.projectId === projectId)

    if (matches.length === 0) return

    try {
        for (const notification of matches) {
            yield call(() => updateDoc(doc(db, 'notifications', notification.id), { watched: true, watchedAt: serverTimestamp() }))
        }
    } catch (err) {
        console.error('Error marking project notifications as watched:', err)
    }
}

// Marks one notification read by its own id — the escape hatch for a notification whose
// task or project no longer exists (nothing else can mark it, so it would stay unread).
function* markNotificationWatchedWorker(action: PayloadAction<string>): Generator<any, void, any> {
    try {
        yield call(() => updateDoc(doc(db, 'notifications', action.payload), { watched: true, watchedAt: serverTimestamp() }))
    } catch (err) {
        console.error('Error marking notification as watched:', err)
    }
}

function* markAllWatchedWorker(action: PayloadAction<{ userId: string; workspaceId: string }>): Generator<any, void, any> {
    const { userId, workspaceId } = action.payload

    try {
        const snapshot = yield call(() =>
            getDocs(
                query(
                    collection(db, 'notifications'),
                    where('userId', '==', userId),
                    where('workspaceId', '==', workspaceId),
                    where('watched', '==', false)
                )
            )
        )

        const batch = writeBatch(db)
        for (const d of snapshot.docs) {
            batch.update(doc(db, 'notifications', d.id), { watched: true, watchedAt: serverTimestamp() })
        }
        yield call(() => batch.commit())
    } catch (err) {
        console.error('Error marking all notifications as watched:', err)
    }
}

// Exported listeners for workspace-level subscriptions

export function* listenNotificationsUnwatched(userId: string, workspaceId: string): Generator<any, void, any> {
    yield put(clearNotifications())
    yield put(setLoading(true))

    logTiming(`[notifs-unwatched] started`)
    const channel = yield call(
        createSnapshotChannel,
        query(
            collection(db, 'notifications'),
            where('userId', '==', userId),
            where('workspaceId', '==', workspaceId),
            where('watched', '==', false),
            orderBy('createdAt', 'desc')
        )
    )

    let firstData = true
    try {
        while (true) {
            const event: any = yield take(channel)
            if (event.type === 'data') {
                const notifications: Notification[] = event.snapshot.docs.map((d: any) => ({
                    id: d.id,
                    ...d.data()
                } as Notification))
                yield put(setNotifications(notifications))
                if (firstData) {
                    logTiming(`[notifs-unwatched] first snapshot (${event.snapshot.size} docs)`)
                    firstData = false
                }
            }
            yield put(setLoading(false))
        }
    } finally {
        channel.close()
    }
}

export function* listenNotificationsAll(userId: string, workspaceId: string): Generator<any, void, any> {
    yield put(clearAllNotifications())
    yield put(setLoadingFull(true))

    logTiming(`[notifs-all] started`)
    // Cap the full-notifications query: notifications accumulate indefinitely, and the inbox
    // only needs the most recent. The separate unwatched listener stays unbounded so the
    // unread badge is accurate even for unread items older than this cap.
    const channel = yield call(
        createSnapshotChannel,
        query(
            collection(db, 'notifications'),
            where('userId', '==', userId),
            where('workspaceId', '==', workspaceId),
            orderBy('createdAt', 'desc'),
            limit(100)
        )
    )

    let firstData = true
    try {
        while (true) {
            const event: any = yield take(channel)
            if (event.type === 'data') {
                const notifications: Notification[] = event.snapshot.docs.map((d: any) => ({
                    id: d.id,
                    ...d.data()
                } as Notification))
                yield put(setAllNotifications(notifications))
                if (firstData) {
                    logTiming(`[notifs-all] first snapshot (${event.snapshot.size} docs)`)
                    firstData = false
                }
            }
            yield put(setLoadingFull(false))
        }
    } finally {
        channel.close()
    }
}

export function* watchNotificationsSaga() {
    yield takeEvery(markWatchedSaga.type, markWatchedWorker)
    yield takeEvery(markProjectWatchedSaga.type, markProjectWatchedWorker)
    yield takeEvery(markNotificationWatchedSaga.type, markNotificationWatchedWorker)
    yield takeLatest(markAllWatchedSaga.type, markAllWatchedWorker)
}
